import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  teachingAgentAPI,
  ChatMessage,
  VideoContent,
  BookContent,
  SocraticQuestion,
  Celebration,
  ToolCall,
} from '../services/teachingAgent';
import type { RailAction } from '../services/sse/railAction';
import { mockTeachingAgentAPI } from '../services/teachingAgentMock';
import { useThinkingTrace } from '../components/TutorChat/useThinkingTrace';
import type { TraceState } from '../components/TutorChat/traceState';

// Use mock API in development when REACT_APP_USE_MOCK_DATA is true
const USE_MOCK = process.env.REACT_APP_USE_MOCK_DATA === 'true';
const api = USE_MOCK ? mockTeachingAgentAPI : teachingAgentAPI;

/** Rail actions (tutor-agent `rail_action`) are opt-in: unset/false keeps today's behaviour. Read at call time. */
const railActionsEnabled = () => process.env.REACT_APP_RAIL_ACTIONS === 'true';

/**
 * useTeachingAgent Hook
 *
 * Provides teaching agent functionality including:
 * - Streaming chat with SSE
 * - Session management
 * - Content display (videos, books)
 * - Socratic question handling
 * - Celebration animations
 */

export interface UseTeachingAgentOptions {
  sessionId?: string;
  /** Canonical course-item UUID. The service resolves all related authority. */
  courseItemId?: string;
  /** Canonical UUID, or a course slug that the server verifies against item. */
  courseId?: string;
  /**
   * Restore the active session after a same-tab reload. The key is bound to
   * the verified OIDC subject and learning context; the server still performs
   * the authoritative ownership and active-status checks.
   */
  persistence?: {
    subject: string;
    context: string;
  };
  labContext?: {
    labId: string;
    labTitle: string;
    instructions: string;
    objectives: string[];
  };
  onVideoShow?: (video: VideoContent) => void;
  onBookShow?: (book: BookContent) => void;
  onQuestionAsk?: (question: SocraticQuestion) => void;
  onCelebration?: (celebration: Celebration) => void;
  /** Called for each validated rail action when REACT_APP_RAIL_ACTIONS=true. */
  onRailAction?: (action: RailAction) => void;
}

export interface UseTeachingAgentReturn {
  // State
  messages: ChatMessage[];
  isStreaming: boolean;
  isThinking: boolean;
  currentTool: ToolCall | null;
  currentVideo: VideoContent | null;
  currentBook: BookContent | null;
  currentQuestion: SocraticQuestion | null;
  /** Rail actions offered by the latest turn (empty unless REACT_APP_RAIL_ACTIONS=true). */
  railActions: RailAction[];
  dismissRailActions: () => void;
  error: Error | null;
  sessionId: string | null;
  /** Live "How Ako is thinking" state for the in-flight / last turn. */
  trace?: TraceState;
  /**
   * True while the stored conversation for this context is still being
   * located and validated. A send made meanwhile waits for it.
   */
  isRestoring: boolean;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  ensureSession: () => Promise<string>;
  interrupt: () => Promise<void>;
  clearMessages: () => void;
  startSession: (initialGoal?: string) => Promise<string>;
  endSession: () => Promise<void>;
  dismissVideo: () => void;
  dismissBook: () => void;
  dismissQuestion: () => void;
}

/** Most recent turns shown when a conversation is restored. */
export const RESTORED_HISTORY_LIMIT = 20;

const SESSION_STORAGE_PREFIX = 'hauraki.companion.session.v1';
const SESSION_CONTEXT_PREFIX = 'hauraki-learning-context-v1:';
const MAX_SESSION_CONTEXT_LENGTH = 512;
const SAFE_SESSION_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const CONTEXT_CHANGED_ERROR =
  'Teaching context changed while the session was starting';

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

export function teachingSessionContextSummary(context: string): string | null {
  if (
    !context
    || context.length > MAX_SESSION_CONTEXT_LENGTH
    || context.trim() !== context
    || hasControlCharacter(context)
  ) {
    return null;
  }
  return `${SESSION_CONTEXT_PREFIX}${context}`;
}

export function teachingSessionStorageKey(
  persistence: UseTeachingAgentOptions['persistence'],
): string | null {
  if (!persistence?.subject || !persistence.context) return null;
  return [
    SESSION_STORAGE_PREFIX,
    encodeURIComponent(persistence.subject),
    encodeURIComponent(persistence.context),
  ].join(':');
}

function readPersistedSession(key: string): string | null {
  try {
    const value = sessionStorage.getItem(key);
    return value && SAFE_SESSION_ID.test(value) ? value : null;
  } catch {
    return null;
  }
}

function writePersistedSession(key: string, sessionId: string): void {
  if (!SAFE_SESSION_ID.test(sessionId)) return;
  try {
    sessionStorage.setItem(key, sessionId);
  } catch {
    // Storage is an enhancement. An authenticated live session still works
    // when the browser blocks sessionStorage.
  }
}

function removePersistedSession(key: string, expectedSessionId?: string): void {
  try {
    if (
      expectedSessionId === undefined ||
      sessionStorage.getItem(key) === expectedSessionId
    ) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // See writePersistedSession.
  }
}

export function useTeachingAgent(options: UseTeachingAgentOptions): UseTeachingAgentReturn {
  const { labContext, onVideoShow, onBookShow, onQuestionAsk, onCelebration, onRailAction } = options;
  const persistenceKey = useMemo(
    () => teachingSessionStorageKey(options.persistence),
    [options.persistence?.context, options.persistence?.subject],
  );
  const persistenceContext = options.persistence?.context;
  const expectedContextSummary = useMemo(
    () => persistenceContext
      ? teachingSessionContextSummary(persistenceContext)
      : null,
    [persistenceContext],
  );
  const learningContextKey = useMemo(
    () => JSON.stringify([
      persistenceKey ?? 'unpersisted',
      labContext?.labId ?? null,
      options.courseItemId ?? null,
      options.courseId ?? null,
    ]),
    [labContext?.labId, options.courseId, options.courseItemId, persistenceKey],
  );

  // State
  const [sessionId, setSessionId] = useState<string | null>(options.sessionId || null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [currentTool, setCurrentTool] = useState<ToolCall | null>(null);
  const [currentVideo, setCurrentVideo] = useState<VideoContent | null>(null);
  const [currentBook, setCurrentBook] = useState<BookContent | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<SocraticQuestion | null>(null);
  const [railActions, setRailActions] = useState<RailAction[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [settledKey, setSettledKey] = useState<string | null>(null);

  // Refs for streaming content accumulation
  const streamingContentRef = useRef('');
  const thinkingTrace = useThinkingTrace();
  const { callbacks: traceCallbacks, begin: beginTrace, markStopped: markTraceStopped } = thinkingTrace;
  const messageIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const activeAssistantIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(options.sessionId || null);
  const sessionCreationRef = useRef<{
    key: string;
    promise: Promise<string>;
  } | null>(null);
  const restorationRef = useRef<{
    key: string;
    promise: Promise<string | null>;
  } | null>(null);
  const restoredKeyRef = useRef<string | null>(null);
  const activePersistenceKeyRef = useRef<string | null>(
    options.sessionId ? persistenceKey : null,
  );
  const activeLearningContextKeyRef = useRef<string | null>(
    options.sessionId ? learningContextKey : null,
  );
  const currentPersistenceKeyRef = useRef(persistenceKey);
  currentPersistenceKeyRef.current = persistenceKey;
  const currentLearningContextKeyRef = useRef(learningContextKey);
  currentLearningContextKeyRef.current = learningContextKey;

  const createSessionOptions = useMemo(
    () => ({
      ...(labContext?.labId ? { labId: labContext.labId } : {}),
      ...(options.courseItemId ? { courseItemId: options.courseItemId } : {}),
      ...(options.courseId ? { courseId: options.courseId } : {}),
      ...(persistenceContext ? { persistenceContext } : {}),
    }),
    [labContext?.labId, options.courseId, options.courseItemId, persistenceContext],
  );

  /**
   * Restore only a server-confirmed, owned, active session. History loading is
   * part of restoration: if it fails, sending stays blocked instead of
   * silently resuming without the learner-visible conversation context.
   */
  const restorePersistedSession = useCallback(async (): Promise<string | null> => {
    const key = persistenceKey;
    if (!key) return null;
    const restorationKey = learningContextKey;
    if (
      activePersistenceKeyRef.current === key &&
      activeLearningContextKeyRef.current === restorationKey &&
      sessionIdRef.current
    ) {
      return sessionIdRef.current;
    }
    if (restoredKeyRef.current === restorationKey) return null;
    if (restorationRef.current?.key === restorationKey) {
      return restorationRef.current.promise;
    }

    const findServerSession = async (): Promise<string | null> => {
      if (!options.courseItemId || !expectedContextSummary || !options.persistence?.subject) return null;
      try {
        const listed = typeof api.listActiveSessions === 'function'
          ? await api.listActiveSessions(options.persistence.subject)
          : [];
        const match = listed.find((candidate) =>
          candidate.status === 'active'
          && candidate.courseItemId === options.courseItemId
          && candidate.contextSummary === expectedContextSummary
          && (!labContext?.labId || candidate.labId === labContext.labId));
        return match?.sessionId && SAFE_SESSION_ID.test(match.sessionId) ? match.sessionId : null;
      } catch {
        // Recovery is an enhancement; never block the learner on it.
        return null;
      }
    };

    const promise = (async () => {
      let persistedSessionId = readPersistedSession(key);
      let adoptedFromServer = false;
      if (!persistedSessionId) {
        // A fresh tab has no stored id. Ask the server for this learner's
        // active sessions and take only one bound to exactly this item and
        // learning context; the usual validation below still applies.
        persistedSessionId = await findServerSession();
        adoptedFromServer = persistedSessionId !== null;
      }
      if (!persistedSessionId) {
        removePersistedSession(key);
        restoredKeyRef.current = restorationKey;
        return null;
      }

      try {
        const session = await api.getSession(persistedSessionId);
        const contextMatches =
          expectedContextSummary !== null
          && session?.contextSummary === expectedContextSummary;
        const labMatches =
          !labContext?.labId
          || session?.labId === labContext.labId;
        const itemMatches = !options.courseItemId || session?.courseItemId === options.courseItemId;
        // A slug is resolved to a UUID by the server; item identity remains
        // authoritative for that case, not an invalid slug-to-UUID compare.
        const courseMatches = !options.courseId
          || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(options.courseId)
          || session?.courseId === options.courseId;
        if (
          !session
          || session.status !== 'active'
          || !contextMatches
          || !labMatches
          || !itemMatches
          || !courseMatches
        ) {
          removePersistedSession(key, persistedSessionId);
          restoredKeyRef.current = restorationKey;
          return null;
        }

        const history = await api.getHistory(persistedSessionId);
        if (
          currentPersistenceKeyRef.current !== key
          || currentLearningContextKeyRef.current !== restorationKey
        ) {
          return null;
        }

        sessionIdRef.current = persistedSessionId;
        activePersistenceKeyRef.current = key;
        activeLearningContextKeyRef.current = restorationKey;
        restoredKeyRef.current = restorationKey;
        setSessionId(persistedSessionId);
        setMessages(history.slice(-RESTORED_HISTORY_LIMIT));
        setError(null);
        if (adoptedFromServer) writePersistedSession(key, persistedSessionId);
        return persistedSessionId;
      } catch (err) {
        const restoreError = err instanceof Error
          ? err
          : new Error('Failed to restore teaching session');
        if (currentPersistenceKeyRef.current === key) {
          setError(restoreError);
        }
        throw restoreError;
      }
    })();
    let trackedPromise: Promise<string | null>;
    trackedPromise = promise.finally(() => {
      if (
        restorationRef.current?.key === restorationKey
        && restorationRef.current.promise === trackedPromise
      ) {
        restorationRef.current = null;
      }
    });

    restorationRef.current = { key: restorationKey, promise: trackedPromise };
    return trackedPromise;
  }, [
    expectedContextSummary,
    labContext?.labId,
    options.courseId,
    options.courseItemId,
    options.persistence?.subject,
    learningContextKey,
    persistenceKey,
  ]);

  /**
   * Start a new teaching session
   */
  const startSession = useCallback(async (initialGoal?: string): Promise<string> => {
    const contextKey = learningContextKey;
    try {
      const session = await api.createSession(createSessionOptions);
      if (currentLearningContextKeyRef.current !== contextKey) {
        throw new Error(CONTEXT_CHANGED_ERROR);
      }

      sessionIdRef.current = session.sessionId;
      activePersistenceKeyRef.current = persistenceKey;
      activeLearningContextKeyRef.current = contextKey;
      setSessionId(session.sessionId);
      setMessages([]);
      setError(null);
      if (persistenceKey) {
        writePersistedSession(persistenceKey, session.sessionId);
      }

      // If there's an initial goal, send it
      if (initialGoal) {
        // Small delay to ensure session is ready
        setTimeout(() => {
          if (currentLearningContextKeyRef.current === contextKey) {
            void sendMessageInternal(session.sessionId, initialGoal);
          }
        }, 100);
      }

      return session.sessionId;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to start session');
      if (currentLearningContextKeyRef.current === contextKey) {
        setError(error);
      }
      throw error;
    }
  }, [
    createSessionOptions,
    learningContextKey,
    persistenceKey,
  ]);

  /**
   * Return the current public teaching-session id, or create an empty
   * authenticated session. A shared promise prevents two quick actions (for
   * example opening HITL and sending chat) from creating competing sessions.
   */
  const ensureSession = useCallback(async (): Promise<string> => {
    const contextKey = learningContextKey;
    if (
      sessionIdRef.current &&
      activeLearningContextKeyRef.current === contextKey
    ) {
      return sessionIdRef.current;
    }

    const restoredSessionId = await restorePersistedSession();
    if (currentLearningContextKeyRef.current !== contextKey) {
      throw new Error(CONTEXT_CHANGED_ERROR);
    }
    if (restoredSessionId) return restoredSessionId;

    if (sessionCreationRef.current?.key === contextKey) {
      return sessionCreationRef.current.promise;
    }

    const creation = api
      .createSession(createSessionOptions)
      .then((session) => {
          if (currentLearningContextKeyRef.current !== contextKey) {
            throw new Error(CONTEXT_CHANGED_ERROR);
          }
          sessionIdRef.current = session.sessionId;
          activePersistenceKeyRef.current = persistenceKey;
          activeLearningContextKeyRef.current = contextKey;
          setSessionId(session.sessionId);
          setError(null);
          if (persistenceKey) {
            writePersistedSession(persistenceKey, session.sessionId);
          }
          return session.sessionId;
        });
    let trackedCreation: Promise<string>;
    trackedCreation = creation.finally(() => {
      if (
        sessionCreationRef.current?.key === contextKey
        && sessionCreationRef.current.promise === trackedCreation
      ) {
        sessionCreationRef.current = null;
      }
    });
    sessionCreationRef.current = {
      key: contextKey,
      promise: trackedCreation,
    };
    return trackedCreation;
  }, [
    createSessionOptions,
    learningContextKey,
    persistenceKey,
    restorePersistedSession,
  ]);

  /**
   * Internal send message (uses provided sessionId)
   */
  const sendMessageInternal = useCallback(async (sid: string, message: string) => {
    if (!sid || isStreaming) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: `msg-${++messageIdRef.current}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Reset state
    setIsStreaming(true);
    setIsThinking(true);
    setError(null);
    streamingContentRef.current = '';
    beginTrace();

    // Create placeholder assistant message
    // The client id is kept until the server's stable messageId arrives in the
    // done event; every update goes through this ref so it survives the swap.
    const clientAssistantId = `msg-${++messageIdRef.current}`;
    let assistantMessageId = clientAssistantId;
    const controller = new AbortController();
    abortRef.current = controller;
    activeAssistantIdRef.current = clientAssistantId;
    setRailActions([]);
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      await api.sendMessageStreaming(
        {
          sessionId: sid,
          message,
          labContext,
        },
        {
          onStart: () => {
            setIsThinking(false);
          },
          onStatus: traceCallbacks.onStatus,
          onReasoning: traceCallbacks.onReasoning,
          onAbort: () => {
            traceCallbacks.onAbort();
            setIsStreaming(false);
            setIsThinking(false);
            setCurrentTool(null);
          },
          onContent: (text) => {
            traceCallbacks.onContent();
            streamingContentRef.current += text;
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: streamingContentRef.current }
                  : msg
              )
            );
          },
          onToolCall: (tool) => {
            setCurrentTool(tool);
            if (tool.status === 'completed' || tool.status === 'failed') {
              setTimeout(() => setCurrentTool(null), 1000);
            }
          },
          onVideo: (video) => {
            setCurrentVideo(video);
            onVideoShow?.(video);
          },
          onBook: (book) => {
            setCurrentBook(book);
            onBookShow?.(book);
          },
          onQuestion: (question) => {
            setCurrentQuestion(question);
            onQuestionAsk?.(question);
          },
          onCelebration: (celebration) => {
            onCelebration?.(celebration);
          },
          onPulse: (pulse) => {
            if (controller.signal.aborted) return;
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, metadata: { ...msg.metadata, pulse } }
                  : msg
              )
            );
          },
          onRailAction: (action) => {
            if (!railActionsEnabled() || controller.signal.aborted) return;
            setRailActions((prev) => [...prev, action]);
            onRailAction?.(action);
          },
          onEnd: (meta) => {
            if (controller.signal.aborted) return;
            traceCallbacks.onEnd();
            if (meta?.messageId || meta?.sources || meta?.stopped) {
              const previousId = assistantMessageId;
              if (meta.messageId) assistantMessageId = meta.messageId;
              activeAssistantIdRef.current = assistantMessageId;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === previousId
                    ? {
                        ...msg,
                        id: assistantMessageId,
                        metadata: {
                          ...msg.metadata,
                          ...(meta.messageId ? { messageId: meta.messageId } : {}),
                          ...(meta.sources ? { sources: meta.sources } : {}),
                          ...(meta.stopped ? { stopped: true } : {}),
                        },
                      }
                    : msg
                )
              );
            }
            setIsStreaming(false);
            setIsThinking(false);
            setCurrentTool(null);
          },
          onError: (err) => {
            if (controller.signal.aborted) return;
            traceCallbacks.onError();
            setError(err);
            setIsStreaming(false);
            setIsThinking(false);
          },
        },
        controller.signal
      );
    } catch (err) {
      // A user-initiated Stop surfaces as an AbortError; interrupt() already
      // settled the UI and kept the partial reply, so it is not a failure.
      if (controller.signal.aborted) return;
      const error = err instanceof Error ? err : new Error('Failed to send message');
      traceCallbacks.onError();
      setError(error);
      setIsStreaming(false);
      setIsThinking(false);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [labContext, isStreaming, onVideoShow, onBookShow, onQuestionAsk, onCelebration, onRailAction, beginTrace, traceCallbacks]);

  /**
   * Send a message (public API)
   */
  const sendMessage = useCallback(async (message: string) => {
    const sid = await ensureSession();
    await sendMessageInternal(sid, message);
  }, [ensureSession, sendMessageInternal]);

  /**
   * Interrupt the current response
   */
  const interrupt = useCallback(async () => {
    // Stop locally first so the learner is never stuck behind a slow network:
    // abort the stream, keep what has been written, and mark it stopped.
    const controller = abortRef.current;
    const stoppedId = activeAssistantIdRef.current;
    if (controller && !controller.signal.aborted) {
      controller.abort();
      if (stoppedId) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === stoppedId
              ? { ...msg, metadata: { ...msg.metadata, stopped: true } }
              : msg
          )
        );
      }
    }
    markTraceStopped();
    setIsStreaming(false);
    setIsThinking(false);
    setCurrentTool(null);
    const sid = sessionIdRef.current ?? sessionId;
    if (!sid) return;
    try {
      await api.interrupt(sid);
    } catch (err) {
      // Best effort: the local stream is already closed.
      console.error('Failed to interrupt:', err);
    }
  }, [sessionId, markTraceStopped]);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    streamingContentRef.current = '';
  }, []);

  /**
   * End the current session
   */
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      await api.endSession(sessionId);
      if (activePersistenceKeyRef.current) {
        removePersistedSession(activePersistenceKeyRef.current, sessionId);
      }
      sessionIdRef.current = null;
      activePersistenceKeyRef.current = null;
      activeLearningContextKeyRef.current = null;
      setSessionId(null);
      clearMessages();
    } catch (err) {
      console.error('Failed to end session:', err);
    }
  }, [sessionId, clearMessages]);

  /**
   * Dismiss content displays
   */
  const dismissVideo = useCallback(() => setCurrentVideo(null), []);
  const dismissBook = useCallback(() => setCurrentBook(null), []);
  const dismissRailActions = useCallback(() => setRailActions([]), []);
  const dismissQuestion = useCallback(() => setCurrentQuestion(null), []);

  // Load existing session if provided
  useEffect(() => {
    if (options.sessionId && options.sessionId !== sessionId) {
      sessionIdRef.current = options.sessionId;
      activeLearningContextKeyRef.current = learningContextKey;
      setSessionId(options.sessionId);
      // Load message history
      api.getHistory(options.sessionId)
        .then(history => setMessages(history))
        .catch(err => console.error('Failed to load history:', err));
    }
  }, [learningContextKey, options.sessionId, sessionId]);

  // Restore the scoped Companion session on mount/reload and whenever the
  // authoritative learning context changes. A context transition never adopts
  // a session stored under a different learner or item key.
  useEffect(() => {
    if (options.sessionId) return;

    if (activeLearningContextKeyRef.current !== learningContextKey) {
      sessionIdRef.current = null;
      activePersistenceKeyRef.current = null;
      activeLearningContextKeyRef.current = null;
      setSessionId(null);
      setMessages([]);
    }

    if (!persistenceKey) return;

    const settled = learningContextKey;
    void restorePersistedSession()
      .catch(() => {
        // Error state is surfaced by AgentStatus. Do not create a replacement
        // session automatically when ownership/history validation is unavailable.
      })
      .then(() => {
        if (currentLearningContextKeyRef.current === settled) setSettledKey(settled);
      });
  }, [
    learningContextKey,
    options.sessionId,
    persistenceKey,
    restorePersistedSession,
  ]);

  return {
    // State
    messages,
    isStreaming,
    isThinking,
    currentTool,
    currentVideo,
    currentBook,
    currentQuestion,
    railActions,
    dismissRailActions,
    error,
    sessionId,
    trace: thinkingTrace.trace,
    isRestoring: !!persistenceKey && !options.sessionId && settledKey !== learningContextKey,

    // Actions
    sendMessage,
    ensureSession,
    interrupt,
    clearMessages,
    startSession,
    endSession,
    dismissVideo,
    dismissBook,
    dismissQuestion,
  };
}

export default useTeachingAgent;
